import { jsx } from 'panel';
import range from 'lodash/range';
import take from 'lodash/take';
import { ItemsMenuEl } from 'common/components/items-menu';
import { InputEl } from 'common/components/input';
import { ChartLegendTheme } from './constants';

function rollUpExtraItemsIntoOther(items, maxSections) {
  // Only show a rolled up "Other" label if legend items are greater than max
  if (maxSections && items.length > maxSections) {
    // the - 1 is because we truncate 1 section in favor of the "Other" variable
    // (e.g. if we have 7 items and 6 max, then we need to only show the first 5 since the 6th will be the "other"
    // with the last two items
    const truncatedLegendSections = take(items, maxSections - 1);
    const hiddenSections = items.slice(maxSections - 1);
    truncatedLegendSections.push({
      label: "Next ".concat(hiddenSections.length),
      tooltipSections: hiddenSections
    });
    items = truncatedLegendSections;
  }

  return items;
}

function LegendLoader() {
  return jsx("div", {
    sel: '.loader'
  }, range(9).map((_, idx) => jsx("div", {
    sel: '.mp-skeleton-large',
    key: idx
  })));
}

function TooltipSections(_ref) {
  let {
    tooltipSections
  } = _ref;
  return jsx("div", {
    sel: '.legend-rollup-segments'
  }, tooltipSections.map(_ref2 => {
    let {
      color,
      label
    } = _ref2;
    return jsx("div", {
      sel: '.segment-text',
      style: {
        '--color': color
      }
    }, label);
  }));
}

function ReadOnlyLegend(_ref3) {
  let {
    selectedSections
  } = _ref3;
  return jsx("div", {
    sel: '.legend-container.legend-readonly'
  }, selectedSections.map((section, idx) => jsx(null, null, idx > 0 ? jsx("div", {
    sel: '.section-divider'
  }) : null, section.items.map((item, idx) => jsx("div", {
    sel: '.legend-segment',
    key: idx
  }, jsx("div", {
    sel: '.segment-icon'
  }, jsx("div", {
    sel: '.readonly-item-color',
    style: {
      'background-color': item.color
    }
  })), jsx("div", {
    sel: '.segment-text'
  }, jsx("span", null, item.label)))))));
}

function CardTableLegend(_ref4) {
  let {
    sections,
    maxItems,
    maxItemWidth
  } = _ref4;
  // Only allow one section for card legends
  let {
    items
  } = sections[0];
  items = rollUpExtraItemsIntoOther(items, maxItems);
  return jsx("div", {
    sel: '.legend-container.legend-table'
  }, jsx("div", {
    sel: '.legend-line'
  }, items.map((item, itemIdx) => jsx("div", {
    sel: '.legend-segment',
    key: itemIdx,
    style: {
      'max-width': maxItemWidth !== null && maxItemWidth !== void 0 ? maxItemWidth : ""
    }
  }, item.label ? jsx("div", {
    sel: '.segment-text',
    attrs: {
      title: item.label
    },
    style: {
      '--color': item.color
    }
  }, item.label) : null, item.tooltipSections && jsx(TooltipSections, {
    tooltipSections: item.tooltipSections
  })))));
}

function CardListLegend(_ref5) {
  let {
    sections,
    maxItems
  } = _ref5;
  // Only allow one section for card legends
  let {
    items
  } = sections[0];
  items = rollUpExtraItemsIntoOther(items, maxItems);
  return jsx("div", {
    sel: '.legend-container.legend-list'
  }, items.map((item, idx) => {
    const text = item.label;
    return jsx("div", {
      sel: '.legend-segment',
      key: idx
    }, jsx("div", {
      sel: '.segment-text',
      style: {
        '--color': item.color
      },
      attrs: {
        title: text
      }
    }, text), item.tooltipSections && jsx(TooltipSections, {
      tooltipSections: item.tooltipSections
    }));
  }));
}

function PrimaryLegend(_ref6) {
  let {
    sections,
    searchQuery,
    searchInput,
    showSelectAll,
    selectAllDisabled,
    selectAllTooltip,
    height,
    onClickItem,
    onSelectAll
  } = _ref6;
  return jsx("div", {
    sel: '.chart-legend-container'
  }, jsx(ItemsMenuEl, {
    style: {
      'max-height': height
    },
    attrs: {
      elref: "itemsMenu",
      multiple: true,
      'search-filter': searchQuery,
      'show-select-all': showSelectAll,
      'select-all-disabled': selectAllDisabled,
      'select-all-tooltip': selectAllTooltip
    },
    props: {
      sections
    },
    on: {
      clickItem: onClickItem,
      selectAll: onSelectAll
    },
    hook: {
      insert: _ref7 => {
        let {
          elm
        } = _ref7;
        return elm.keydownProxyEl = searchInput;
      }
    }
  }));
}

export function template() {
  var _a;

  const {
    searchQuery
  } = this.state;
  const sections = (_a = this.sections) !== null && _a !== void 0 ? _a : this.attr("sections") || this.helpers.getSections();
  const loading = this.attr("loading");
  const theme = this.attr("theme");
  let themedLegend;

  switch (theme) {
    case ChartLegendTheme.ReadOnly:
      themedLegend = jsx(ReadOnlyLegend, {
        selectedSections: this.helpers.filterSelected(sections)
      });
      break;

    case ChartLegendTheme.CardList:
      themedLegend = jsx(CardListLegend, {
        sections: sections,
        maxItems: this.attr("max-legend-items")
      });
      break;

    case ChartLegendTheme.CardTable:
      themedLegend = jsx(CardTableLegend, {
        sections: sections,
        maxItems: this.attr("max-legend-items"),
        maxItemWidth: this.attr("max-table-item-width")
      });
      break;

    default:
      themedLegend = jsx(PrimaryLegend, {
        sections: sections,
        searchQuery: searchQuery,
        searchInput: this.elRefs.searchInput,
        showSelectAll: this.attr("show-select-all"),
        selectAllDisabled: this.attr("select-all-disabled"),
        selectAllTooltip: this.attr("select-all-tooltip"),
        height: this.helpers.getItemsMenuHeight(),
        onClickItem: this.helpers.toggledCheckbox,
        onSelectAll: this.helpers.toggledAllCheckboxes
      });
  }

  return jsx("div", {
    sel: '.chart-legend',
    class: {
      ["chart-theme-".concat(theme)]: true
    },
    style: this.helpers.overrideStyle(),
    attrs: {
      elref: "wrapper"
    }
  }, theme === ChartLegendTheme.Primary && this.attr("enable-search") ? jsx("div", {
    sel: '.chart-legend-search'
  }, jsx(InputEl, {
    attrs: {
      icon: "search",
      placeholder: "Search",
      elref: "search"
    },
    hook: {
      insert: _ref8 => {
        let {
          elm
        } = _ref8;
        return this.setElRef("searchInput", elm);
      }
    },
    on: {
      input: this.helpers.updateLegendSearch
    }
  })) : null, loading ? jsx(LegendLoader, null) : themedLegend);
}